/**
 * FocusMode — full-screen calming timer for prayer / meditation /
 * breathwork / other focused practices.
 *
 * Triggered by:
 *   • Starting a focus-mode-enabled habit as today's mission
 *     (MissionRitual's Set beat — if the source habit has
 *     focusModeEnabled, we open FocusMode instead of deep-linking to
 *     the Habits tab + the floating mission pill).
 *   • Tapping a focus-mode-enabled habit's "Start" affordance in the
 *     Habits tab (future work — not wired in v1).
 *
 * Founder report 2026-06-19 (relayed from Jason): "Right now when I
 * tap prayer, it opens habits and has a purple floating bar… not sure
 * what it is or what I do with it. Ideally tapping prayer opens a
 * full calming screen. Minimal distractions, maybe a short text blip
 * about why prayer is useful, then asks me about how long I'd like
 * my prayer times to last, then a begin prayer button that creates a
 * very dark screen with a countdown."
 *
 * Three screens:
 *   1. Intro — one AI-generated calming sentence (mechanical fallback
 *      per category when the user hasn't accepted the AI dialog).
 *      Continue button.
 *   2. Picker — chips for 3 / 5 / 10 / 15 / 20 min. Defaults to the
 *      habit's `target` when its `unit` is "minutes", else 10.
 *   3. Timer — dark screen, large countdown, one line of guidance
 *      ("Slow your breathing. Close out distractions."). When the
 *      timer hits zero, marks the habit complete via the standard
 *      `POST /api/habits/{id}/logs` path and shows a brief done
 *      state, then closes. "Mark complete" + "Close" buttons let the
 *      user exit early either way.
 *
 * Mounted once in MainApp. Listens for `trybeup:open-focus-mode`
 * custom events with detail `{habitId, title, category, target, unit}`.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { closeButtonClass, CloseIcon } from "@/components/ui/close-button";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { apiRequest } from "@/lib/queryClient";
import { enqueueAction } from "@/lib/offlineQueue";
import { localDateStr } from "@/lib/dateUtils";
import { invalidateDayRecord } from "@/lib/dayRecord";
import { trackEvent } from "@/hooks/useAnalytics";
import { Haptics, NotificationType } from "@capacitor/haptics";
import { useToast } from "@/hooks/use-toast";

export interface FocusModeOpenDetail {
  habitId: string;
  title: string;
  category?: string | null;
  /** Habit's `target` — used to pre-select the duration picker when
   *  `unit` is "minutes". */
  target?: number | null;
  unit?: string | null;
  /** True when Focus Mode was opened from the mission Start beat —
   *  in that case the completion path also fires
   *  `POST /api/missions/complete` so the `ActiveMissionBar` purple
   *  pill dismisses itself (founder report: "the bar is still here
   *  after the timer finishes — what do I do with it?"). False
   *  (default) when opened from a direct Habits-tab tap: there's
   *  no mission to complete, and firing it would erroneously
   *  complete a DIFFERENT mission set for today. */
  fromMission?: boolean;
}

const OPEN_EVENT = "trybeup:open-focus-mode";

const BASE_DURATION_CHIPS = [3, 5, 10, 15, 20] as const;

type Screen = "intro" | "picker" | "timer" | "done";

function formatClock(secondsRemaining: number): string {
  const safe = Math.max(0, Math.floor(secondsRemaining));
  const m = Math.floor(safe / 60);
  const s = safe % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

/** True when the habit carries an explicit minutes target we should
 *  honour (Prayer: 15, Quiet Time: 10, a 30-min meditation, etc.). */
function minutesTarget(target?: number | null, unit?: string | null): number | null {
  if (typeof target === "number" && target > 0 && unit && unit.toLowerCase().startsWith("min")) {
    return target;
  }
  return null;
}

function defaultDurationFromHabit(target?: number | null, unit?: string | null): number {
  // Honour the habit's own minutes target EXACTLY — a 30-min prayer
  // must default to 30, not get clamped to the nearest chip (the old
  // snap silently shrank long sessions, the exact case this feature
  // is for). Falls back to 10 when there's no usable minutes target.
  return minutesTarget(target, unit) ?? 10;
}

/** The duration chips to render. Base set plus the habit's own target
 *  if it isn't already one of them, sorted — so a 30-min habit shows a
 *  selectable "30" chip instead of being truncated to 20. */
function durationChipsFor(target?: number | null, unit?: string | null): number[] {
  const mt = minutesTarget(target, unit);
  if (mt !== null && !BASE_DURATION_CHIPS.includes(mt as typeof BASE_DURATION_CHIPS[number])) {
    return [...BASE_DURATION_CHIPS, mt].sort((a, b) => a - b);
  }
  return [...BASE_DURATION_CHIPS];
}

// ── Session persistence ──────────────────────────────────────────────
// An in-flight timer is stored in localStorage keyed on an ABSOLUTE end
// timestamp (not a remaining-seconds countdown) — the same model the
// workout timer uses (ActiveWorkoutSticky). Storing the end time means
// closing/backgrounding the app doesn't lose the session: on reopen we
// recompute remaining = endTime − now, so real elapsed time is honoured
// (a 10-min prayer you close at 3:00 and reopen at 6:00 shows 4:00 left,
// and one whose time fully elapsed while away comes back completed).
const FOCUS_SESSION_KEY = "trybeup_focus_session";
// Don't resurrect a session older than a day — past that it's almost
// certainly abandoned, and silently logging a day-old prayer on reopen
// would be wrong.
const FOCUS_SESSION_MAX_AGE_MS = 24 * 60 * 60 * 1000;

interface PersistedFocusSession {
  detail: FocusModeOpenDetail;
  durationMin: number;
  endTime: number;
  savedAt: number;
}

function remainingFromEnd(endTime: number): number {
  return Math.max(0, Math.ceil((endTime - Date.now()) / 1000));
}

function loadFocusSession(): PersistedFocusSession | null {
  try {
    const raw = localStorage.getItem(FOCUS_SESSION_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as PersistedFocusSession;
    if (!s?.detail?.habitId || typeof s.endTime !== "number" || typeof s.savedAt !== "number") {
      return null;
    }
    if (Date.now() - s.savedAt > FOCUS_SESSION_MAX_AGE_MS) return null;
    return s;
  } catch {
    return null;
  }
}

function saveFocusSession(s: PersistedFocusSession): void {
  try { localStorage.setItem(FOCUS_SESSION_KEY, JSON.stringify(s)); } catch { /* quota — non-fatal */ }
}

function clearFocusSession(): void {
  try { localStorage.removeItem(FOCUS_SESSION_KEY); } catch { /* no-op */ }
}

export default function FocusMode() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [detail, setDetail] = useState<FocusModeOpenDetail | null>(null);
  const [screen, setScreen] = useState<Screen>("intro");
  const [introText, setIntroText] = useState<string | null>(null);
  const [introLoading, setIntroLoading] = useState(false);
  const [duration, setDuration] = useState<number>(10);
  const [secondsRemaining, setSecondsRemaining] = useState<number>(0);
  // Timer interval id, held in a ref so cleanup never races against
  // setState. The countdown decrements every 1000ms; on hitting zero
  // we clear the interval, mark the habit complete, and advance the
  // screen — none of which should fire twice from a stale tick.
  const tickRef = useRef<number | null>(null);
  // Guards against a double habit-log. Two paths can fire the
  // completion: the auto-complete effect when the timer hits 0:00, and
  // the "Mark complete now" button. If the user taps the button in the
  // ~1s before the timer naturally expires, BOTH fire and we'd POST two
  // logs (double XP / streak). This ref makes completion fire-once per
  // session; reset on open (new session) and on error (so the manual
  // retry path still works).
  const completionFiredRef = useRef(false);
  // Absolute ms timestamp the countdown ends at. Source of truth for the
  // remaining time (survives background/close); the per-second tick just
  // reads it. Null when no timer is running.
  const endTimeRef = useRef<number | null>(null);

  // Listen for the open event. Reset state every time so a previous
  // session's intro doesn't leak into the next open.
  useEffect(() => {
    const handler = (e: Event) => {
      const d = (e as CustomEvent<FocusModeOpenDetail>).detail;
      if (!d?.habitId) return;
      setDetail(d);
      setScreen("intro");
      setIntroText(null);
      setIntroLoading(true);
      setDuration(defaultDurationFromHabit(d.target, d.unit));
      setSecondsRemaining(0);
      completionFiredRef.current = false;
    };
    window.addEventListener(OPEN_EVENT, handler);
    return () => window.removeEventListener(OPEN_EVENT, handler);
  }, []);

  // Restore an in-flight timer on mount (cold start / Capgo reload /
  // reopen). Jumps straight back to the countdown — no intro re-fetch
  // (screen !== "intro"). If the session's time fully elapsed while the
  // app was away, secondsRemaining restores to 0 and the auto-complete
  // effect below logs it just as if the user had watched it hit zero.
  useEffect(() => {
    const s = loadFocusSession();
    if (!s) return;
    endTimeRef.current = s.endTime;
    completionFiredRef.current = false;
    setDetail(s.detail);
    setDuration(s.durationMin);
    setSecondsRemaining(remainingFromEnd(s.endTime));
    setScreen("timer");
  }, []);

  // Recompute remaining the instant the app comes back to the foreground
  // (iOS suspends setInterval in the background, so without this the
  // clock would show a stale value for up to a second on return).
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      if (endTimeRef.current == null) return;
      setSecondsRemaining(remainingFromEnd(endTimeRef.current));
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);

  // Fetch the intro line whenever a new session opens. Independent
  // effect so we can re-fetch on subsequent opens of the same habit
  // (each open gets a fresh AI sentence per the founder's "AI-
  // generated each time" choice).
  useEffect(() => {
    if (!detail || screen !== "intro") return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post("/api/focus/intro", { habitId: detail.habitId });
        if (cancelled) return;
        setIntroText(typeof data?.text === "string" && data.text ? data.text : null);
      } catch {
        // Mechanical fallback rendered inline below — never block the
        // session on a failed intro fetch.
      } finally {
        if (!cancelled) setIntroLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [detail, screen]);

  // The countdown. Decrement once per second; the separate
  // useEffect below watches for secondsRemaining hitting 0 while
  // we're still on the timer screen and handles auto-complete from
  // there — keeps the state updater pure (no nested setState / no
  // setTimeout race against close-and-reopen-for-a-different-habit).
  useEffect(() => {
    if (screen !== "timer") return;
    if (tickRef.current !== null) return;  // already running
    tickRef.current = window.setInterval(() => {
      // Read from the absolute end time, not a decrementing counter, so
      // a backgrounded/throttled interval self-corrects on the next tick.
      const end = endTimeRef.current;
      setSecondsRemaining(end == null ? 0 : remainingFromEnd(end));
    }, 1000);
    return () => {
      if (tickRef.current !== null) {
        window.clearInterval(tickRef.current);
        tickRef.current = null;
      }
    };
  }, [screen]);

  // Auto-complete when the timer hits zero on the timer screen.
  // Separate from the tick so closing-and-reopening for a different
  // habit between tick and render can't fire a stray mutate() with
  // the wrong habit in scope — by the time this effect runs, `detail`
  // is whatever React has committed, never a stale snapshot.
  //
  // We DON'T advance to the "done" screen here — that happens in
  // completeMut.onSuccess so the "Logged." confirmation only shows
  // after the write actually lands (or is queued offline). Both this
  // effect and the "Mark complete now" button funnel through
  // fireCompletion(), which is idempotent per session via
  // completionFiredRef — without it, tapping the button in the ~1s
  // before the timer naturally expires would fire two POSTs.
  useEffect(() => {
    if (screen !== "timer" || secondsRemaining > 0) return;
    // Soft success-pattern buzz to mark the moment the timer ends —
    // user can close their eyes during the session and feel the cue
    // without staring at the clock. Same pattern RestTimer + RecoveryTab
    // use. NOT fired on the manual "Mark complete now" tap (iOS gives
    // its own tap haptic there, plus deliberately ending early
    // shouldn't get the "your time is up" celebration). Native-only;
    // .catch eats the rejection on web/desktop where the plugin is
    // a no-op. navigator.vibrate fallback covers web PWA.
    //
    // Gate on "just ended" (endTime within the last few seconds): a
    // persisted session whose timer already expired hours ago restores
    // straight to secondsRemaining=0 on mount and triggers this same
    // auto-complete path. A sudden buzz the instant the user opens
    // the app — for a session they don't remember running — would be
    // jarring. The completion still fires either way; only the haptic
    // is skipped on the stale-expired path.
    const endTime = endTimeRef.current;
    const justEnded = endTime != null && Date.now() - endTime < 5000;
    if (justEnded) {
      Haptics.notification({ type: NotificationType.Success }).catch(() => {
        if (typeof navigator !== "undefined" && navigator.vibrate) navigator.vibrate(150);
      });
    }
    fireCompletion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen, secondsRemaining]);

  const fireCompletion = () => {
    if (completionFiredRef.current) return;
    completionFiredRef.current = true;
    // Stop the countdown the instant we commit to completing — leaving
    // it running burns a re-render every second on the dark screen and
    // is what made the old "implicit" double-fire guard load-bearing.
    if (tickRef.current !== null) {
      window.clearInterval(tickRef.current);
      tickRef.current = null;
    }
    completeMut.mutate();
  };

  const completeMut = useMutation({
    mutationFn: async () => {
      if (!detail) return { offline: false };
      // Use the LOCAL calendar date (not UTC) so a session finished late at
      // night logs against today, not tomorrow, for users west of UTC.
      const localDate = localDateStr();
      const tzOffset = new Date().getTimezoneOffset();
      // Idempotency key so a queued-and-replayed log isn't double-counted
      // (migration 214).
      const client_id =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `c_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
      const body = { value: 1, note: "", localDate, tzOffset, client_id };
      // Offline-safe, same as MyHabits quick-complete: queue the log so
      // a focus session finished on the subway isn't silently lost. The
      // queue flushes on reconnect; from the user's view the session is
      // "done" either way.
      const queueForRetry = (): { offline: true } => {
        // enqueueAction returns null when localStorage fails (Safari Private
        // Mode, quota exhausted). Surface as an error rather than silently
        // dropping the focus-session log.
        const queued = enqueueAction(`/api/habits/${detail.habitId}/logs`, "POST", body);
        if (queued === null) {
          throw new Error("Couldn't save your focus session offline — device storage is full or unavailable.");
        }
        return { offline: true };
      };

      if (!navigator.onLine) return queueForRetry();

      // Online path — but `navigator.onLine` lies on flaky mobile networks, so
      // queue for retry on any UNCONFIRMED failure (network throw or 5xx)
      // rather than losing the session. A 4xx is a real rejection and throws.
      let res: Response;
      try {
        res = await apiRequest("POST", `/api/habits/${detail.habitId}/logs`, body);
      } catch (e) {
        const msg = e instanceof Error ? e.message : "";
        const isNetwork =
          e instanceof TypeError || /load failed|failed to fetch|network/i.test(msg);
        if (isNetwork) return queueForRetry();
        throw e;
      }
      if (!res.ok) {
        if (res.status >= 500) return queueForRetry();
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || err.message || "Failed to log focus session");
      }
      return { offline: false, data: await res.json() };
    },
    onSuccess: async (result) => {
      // The session is done — clear the persisted timer so it can't be
      // resurrected on the next launch.
      endTimeRef.current = null;
      clearFocusSession();
      // Advance to the confirmation screen only now that the write
      // succeeded (or was queued offline) — see the auto-complete
      // effect note above.
      setScreen("done");

      // Analytics parity with the quick-complete paths — without this
      // every focus-mode completion was invisible to the funnel.
      // Fire-and-forget; safe on the offline/queued path too.
      if (detail) {
        trackEvent("habit_completed", {
          habit_id: detail.habitId,
          category: detail.category ?? undefined,
          surface: "focus_mode",
        });
      }

      // Offline / queued-for-retry: the log isn't on the server yet, so DON'T
      // invalidate the habits / snapshot / mission queries — an immediate
      // refetch would show the habit as not-completed and fight the queued
      // write (parity with MyHabits, bug 2026-08-08). The offline-sync callback
      // refreshes once the log lands.
      if (result?.offline) return;

      queryClient.invalidateQueries({ queryKey: ["/api/habits"] });
      queryClient.invalidateQueries({ queryKey: ["/api/missions/today"] });
      // Same snapshot refresh the quick-complete paths do, so the My
      // Day life-area / pillar chips don't stay stale after a focus
      // completion (they did before — only the habits list refreshed).
      queryClient.invalidateQueries({ queryKey: ["/api/today/snapshot"] });
      // The My Day flame, Your days and the Progress tiles read the day record.
      invalidateDayRecord(queryClient);

      // When Focus Mode was opened from the mission Start beat, also
      // mark the mission complete so the ActiveMissionBar purple pill
      // dismisses itself — addresses Jason's "the bar is still here
      // after the timer finishes" follow-up. Fire-and-forget: a
      // hiccup on mission complete shouldn't toast over the user's
      // calm done-state.
      if (detail?.fromMission) {
        try {
          // /api/missions/complete completes whatever mission is set
          // for today by date — it has no source binding. A focus
          // session can run for many minutes, during which the user
          // could swap today's mission (evening review, another
          // device). Guard against stamping an unrelated mission
          // complete: only fire if today's mission STILL points at the
          // habit we just finished.
          const todayRes = await apiRequest("GET", "/api/missions/today");
          const today = todayRes.ok ? await todayRes.json() : null;
          const stillOurs = today?.mission?.source_id === detail.habitId;
          if (stillOurs) {
            await apiRequest("POST", "/api/missions/complete");
            queryClient.invalidateQueries({ queryKey: ["/api/missions/today"] });
            queryClient.invalidateQueries({ queryKey: ["/api/pillars/today"] });
            queryClient.invalidateQueries({ queryKey: ["/api/users/me"] });
          }
        } catch {
          // Logged silently — the habit completion (the primary
          // outcome) already succeeded.
        }
      }
    },
    onError: (err: Error) => {
      // Allow the manual "Mark complete now" retry to fire again.
      completionFiredRef.current = false;
      toast({
        title: "Couldn't log the session",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const close = () => {
    if (tickRef.current !== null) {
      window.clearInterval(tickRef.current);
      tickRef.current = null;
    }
    // Dropping the session here is what makes "End without logging" and
    // the done-screen "Done" button actually end it — otherwise the
    // persisted timer would resurrect on the next launch.
    endTimeRef.current = null;
    clearFocusSession();
    setDetail(null);
  };

  // Chips depend only on the open habit's target/unit. useMemo so the
  // list (and its identity) is stable across the per-second timer
  // re-renders. Must run before the early return to satisfy hooks.
  const chips = useMemo(
    () => durationChipsFor(detail?.target, detail?.unit),
    [detail?.target, detail?.unit],
  );

  const beginTimer = () => {
    // Anchor the countdown to an absolute end time and persist it, so
    // the session survives close/background (restored on mount).
    const endTime = Date.now() + duration * 60 * 1000;
    endTimeRef.current = endTime;
    completionFiredRef.current = false;
    if (detail) {
      saveFocusSession({ detail, durationMin: duration, endTime, savedAt: Date.now() });
    }
    setSecondsRemaining(duration * 60);
    setScreen("timer");
  };

  // ── Render ─────────────────────────────────────────────────────────
  // Two surfaces:
  //   1. The timer is a true dark full-screen overlay (founder brief:
  //      "very dark screen with a countdown"), NOT a Dialog — Dialog
  //      chrome adds rounded corners + an outer backdrop that breaks
  //      the calm look.
  //   2. Intro / Picker / Done use the shared Dialog. The Dialog is
  //      always rendered (even when detail is null) and its `open` is
  //      driven by `dialogOpen` — required for Radix to run its
  //      close-animation + body-style cleanup. If we unmounted the
  //      Dialog abruptly (the prior bug), Radix's overflow-hidden on
  //      the body never got removed → My Day rendered behind a black
  //      scroll-locked surface ("after the prayer the screen black
  //      except the header" — founder report 2026-06-20).
  const dialogOpen = !!detail && (screen === "intro" || screen === "picker" || screen === "done");

  return (
    <>
      {detail && screen === "timer" && (
        <div
          className="fixed inset-0 z-[300] bg-black text-white flex flex-col"
          role="dialog"
          aria-modal="true"
          aria-label={`Focus session — ${detail.title}`}
        >
          <div className="flex items-center justify-between px-5 pt-[max(1rem,env(safe-area-inset-top))]">
            <span className="text-xs uppercase tracking-widest text-white/40">
              {detail.title}
            </span>
            <button
              type="button"
              onClick={close}
              className={closeButtonClass}
              aria-label="Close focus session"
            >
              <CloseIcon />
            </button>
          </div>

          <div className="flex-1 flex flex-col items-center justify-center px-6">
            <div className="text-[5.5rem] sm:text-[7rem] font-light tabular-nums tracking-tight leading-none">
              {formatClock(secondsRemaining)}
            </div>
            <p className="mt-6 text-center text-sm text-white/55 max-w-xs leading-relaxed">
              Slow your breathing. Close out distractions. Give this time your full presence.
            </p>
          </div>

          <div className="px-5 pb-[max(1.5rem,env(safe-area-inset-bottom))] flex flex-col gap-2">
            <Button
              variant="outline"
              className="w-full bg-transparent border-white/20 text-white hover:bg-white/10 hover:text-white"
              onClick={fireCompletion}
              disabled={completeMut.isPending}
            >
              {completeMut.isPending ? "Saving…" : "Mark complete now"}
            </Button>
          </div>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={(v) => { if (!v) close(); }}>
        {/* DialogContent is conditional on detail so derefs of
            detail.title don't fire while the close-out animation runs
            after detail has been nulled. Radix tolerates a Dialog with
            an empty child set fine; it just animates the overlay out. */}
        {detail && (
          <DialogContent className="max-w-sm bg-card">
            <DialogTitle className="text-base font-semibold text-foreground">
              {detail.title}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Focus session for {detail.title}
            </DialogDescription>

            {screen === "intro" && (
              <div className="flex flex-col gap-5 pt-1">
                <div className="min-h-[5rem] text-base leading-relaxed text-foreground/85">
                  {introLoading && !introText ? (
                    <span className="inline-flex items-center gap-2 text-muted-foreground text-sm">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Settling in…
                    </span>
                  ) : (
                    introText ?? "A few minutes of undivided attention changes what comes next."
                  )}
                </div>
                <Button onClick={() => setScreen("picker")} className="w-full">
                  Continue
                </Button>
              </div>
            )}

            {screen === "picker" && (
              <div className="flex flex-col gap-5 pt-1">
                <p className="text-sm text-muted-foreground">How long today?</p>
                <div className="flex flex-wrap gap-2">
                  {chips.map((mins) => {
                    const selected = duration === mins;
                    return (
                      <button
                        key={mins}
                        type="button"
                        onClick={() => setDuration(mins)}
                        className={`rounded-lg py-3 text-sm font-semibold border transition-colors ${
                          selected
                            ? "bg-primary text-primary-foreground border-primary"
                            : "bg-background text-foreground/70 border-border hover:border-primary/40"
                        }`}
                        aria-pressed={selected}
                      >
                        {mins}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-muted-foreground -mt-2">minutes</p>
                <Button onClick={beginTimer} className="w-full mt-1">
                  Begin
                </Button>
              </div>
            )}

            {screen === "done" && (
              <div className="flex flex-col items-center gap-3 pt-2 pb-1">
                <div className="text-3xl">✓</div>
                <p className="text-sm text-foreground/80">
                  Logged. {detail.title} for {duration} {duration === 1 ? "minute" : "minutes"}.
                </p>
                <Button variant="ghost" onClick={close} className="mt-2">
                  Done
                </Button>
              </div>
            )}
          </DialogContent>
        )}
      </Dialog>
    </>
  );
}

/**
 * Helper for callers — dispatches the open event with a typed detail.
 * Keeps the event name + payload shape in one place so the callsites
 * (MissionRitual, HabitCard, etc.) don't drift.
 */
export function openFocusMode(detail: FocusModeOpenDetail): void {
  window.dispatchEvent(new CustomEvent<FocusModeOpenDetail>(OPEN_EVENT, { detail }));
}
